// src/features/scheduleGenerator/components/ScheduleGeneratorSettingsModal.tsx
// @deprecated Day Draft V3: 由 ToneSelectionModal 取代。確認 QA 通過後刪除。
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { addDays, format } from 'date-fns';

interface ScheduleGeneratorSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (date: string, latestNote: string) => void;
  isLoading: boolean;
  isLesson2Active?: boolean;
}

function getDefaultDate(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date().getHours() >= 18 ? addDays(today, 1) : today;
}

const ScheduleGeneratorSettingsModal: React.FC<ScheduleGeneratorSettingsModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  isLoading,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(getDefaultDate);
  const [userNotes, setUserNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedDate(getDefaultDate());
      setUserNotes('');
    }
  }, [isOpen]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = addDays(today, 1);

  const isToday = selectedDate.getTime() === today.getTime();

  const handleSubmit = () => {
    onGenerate(format(selectedDate, 'yyyy-MM-dd'), userNotes);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-modal-nested overflow-y-auto bg-neutral-900 bg-opacity-50 flex justify-center items-end sm:items-center">
      <div className="bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-md sm:mx-auto shadow-xl transform transition-all animate-slideInUp flex flex-col">
        <div className="sm:hidden w-full flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-neutral-300 rounded-full" />
        </div>

        <div className="flex justify-between items-center p-4">
          <h2 className="text-lg font-semibold text-neutral-800">Day Draft</h2>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700" aria-label="Close">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="px-4 pb-4 space-y-4">
          {/* Segmented control */}
          <div
            role="group"
            aria-label="Select day"
            className="flex rounded-full bg-neutral-100 p-1 gap-1"
          >
            <button
              type="button"
              aria-pressed={isToday}
              onClick={() => setSelectedDate(today)}
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-all ${
                isToday
                  ? 'bg-white text-neutral-900 shadow'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              Today
            </button>
            <button
              type="button"
              aria-pressed={!isToday}
              onClick={() => setSelectedDate(tomorrow)}
              className={`flex-1 rounded-full py-2 text-sm font-medium transition-all ${
                !isToday
                  ? 'bg-white text-neutral-900 shadow'
                  : 'text-neutral-500 hover:text-neutral-700'
              }`}
            >
              Tomorrow
            </button>
          </div>

          {/* Notes */}
          <textarea
            rows={3}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-400 focus:border-sky-400 resize-none text-sm text-neutral-700 placeholder:text-neutral-400"
            placeholder="Anything I should know? e.g. &quot;Dinner with family at 7pm, keep evening light&quot;"
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
          />
        </div>

        <div className="flex justify-end gap-3 p-4 pb-safe border-t border-neutral-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white text-neutral-600 border border-neutral-300 rounded-md text-sm font-medium hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary btn-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Generating...
              </>
            ) : (
              'Generate →'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ScheduleGeneratorSettingsModal;
